/**
 * Google Analytics 4.
 *
 * The tag is injected at runtime rather than sitting in index.html so it can be switched off
 * entirely: with no VITE_GA_MEASUREMENT_ID the script is never fetched, which keeps local dev
 * and preview builds out of the production property. Nothing here throws — analytics must
 * never be able to break a page.
 */

import { hasAnalyticsConsent, subscribeToConsent, type ConsentDecision } from "@/lib/consent";

const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Whether a measurement ID is configured for this build. */
export const analyticsEnabled = Boolean(MEASUREMENT_ID);

let initialised = false;

const analyticsStorage = (decision: ConsentDecision | null) =>
  decision === "accepted" ? "granted" : "denied";

/**
 * Load gtag.js and configure the property.
 *
 * Google Consent Mode v2: the tag loads on every visit, but analytics_storage stays "denied"
 * until the visitor accepts. While denied, gtag sets no _ga cookies and sends cookieless pings
 * only; Reject keeps it that way and never grants. Ad signals are always denied — we run no ads.
 *
 * `send_page_view: false` because this is a single-page app: gtag would only ever report the
 * entry URL, so page views are sent manually on navigation instead (see trackPageView).
 */
export function initAnalytics(): void {
  if (initialised || !MEASUREMENT_ID) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  initialised = true;

  window.dataLayer = window.dataLayer || [];
  // gtag must push `arguments` itself — a rest array is not the same shape and breaks the tag.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };

  // Must precede "config": consent defaults only apply to hits queued after them.
  window.gtag("consent", "default", {
    analytics_storage: hasAnalyticsConsent() ? "granted" : "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  subscribeToConsent((decision) => {
    window.gtag?.("consent", "update", { analytics_storage: analyticsStorage(decision) });
  });

  window.gtag("js", new Date());
  window.gtag("config", MEASUREMENT_ID, { send_page_view: false });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
  document.head.appendChild(script);
}

/**
 * Report a page view. Public booking URLs carry the host's username and event slug, so the
 * path is sent as-is — it is already public — but the query string is dropped, since it can
 * carry a booking id or checkout token that has no business in analytics.
 */
export function trackPageView(path: string, title?: string): void {
  if (!MEASUREMENT_ID || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: `${window.location.origin}${path}`,
    page_title: title ?? document.title,
  });
}

/** Report a custom event. No-ops when analytics is disabled. */
export function trackEvent(name: string, params?: Record<string, unknown>): void {
  if (!MEASUREMENT_ID || !window.gtag) return;
  window.gtag("event", name, params ?? {});
}
